import { lazy, Suspense, useMemo, type ReactNode } from 'react';
import { Refine, Authenticated, CanAccess, type AuthProvider } from '@refinedev/core';
import routerProvider, { NavigateToResource, CatchAllNavigate } from '@refinedev/react-router';
import { Route, Routes, Outlet, useLocation } from 'react-router';
import { Provider as ReduxProvider } from 'react-redux';
import { store } from '@admin-panel/store';
import { App as AntdApp, Typography, ConfigProvider } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  useNotificationProvider,
  ThemedLayout as ThemedLayoutV2,
  ErrorComponent,
} from '@refinedev/antd';
import LKLayout from '@admin-panel/components/lib/Layout';
import { AccessControlClient } from '@admin-panel/providers/lib/access/accesscontrolprovider';
import { LKAuthProvider } from '@admin-panel/providers/lib/authProvider';
import { restDataProvider } from '@admin-panel/providers/lib/dataprovider';
import { ThemeProvider, useTheme } from '@admin-panel/config/theme';
import { AccessControlConfig, getResources, PATHS_NAMES } from '@admin-panel/access-control';
import '@refinedev/antd/dist/reset.css';
import 'react-quill/dist/quill.snow.css';
import { GlobalProvider, useGlobalContext, useIsOnline } from '@admin-panel/hooks';
import { FoundationLoader } from '@admin-panel/ui';
import { lazyNamedExport } from '@admin-panel/utils';
import { useAntdTheme } from 'antd-style';
import './global.css';
import { unlockHubAlarmAudio } from './hubAlerts/hubAlarm';
import { markBriefAfterLogin } from './hubAlerts/hubBriefingSession';
import {
  HubAlertSetupGate,
  HubAlertSetupProvider,
  SETUP_PATH,
} from './hubAlertSetup/HubAlertSetupGate';
import HubHeaderTools from './headerTools/HubHeaderTools';
import SetupReporter from './hubAlertSetup/SetupReporter';
import AlertCenter from './hubAlerts/alertCenter/AlertCenter';
import { AlertCenterBoundary } from './hubAlerts/alertCenter/AlertCenterBoundary';
import { discardInbox } from './hubAlerts/alertCenter/alertCenterStore';
import { resetRegistrationStatus } from './hubAlertSetup/hubAlertSetupState';
import ReleaseWatcher from './releaseWatcher/ReleaseWatcher';
import AppLinkHandler from './appLinks/AppLinkHandler';
import { PageErrorBoundary } from './PageErrorBoundary';

const HubAlerts = lazy(() => import('./hubAlerts/HubAlerts'));
const HubAlertSetupPage = lazy(() => import('./hubAlertSetup/HubAlertSetupPage'));
const Login = lazyNamedExport<{ title?: string }>(
  () => import('@admin-panel/pages/auth/login'),
  'Login',
);
const WarehouseForgotPassword = lazy(
  () => import('@admin-panel/pages/auth/WarehouseForgotPassword'),
);
const WarehouseResetPassword = lazy(() => import('@admin-panel/pages/auth/WarehouseResetPassword'));
const HomeDashboard = lazyNamedExport(
  () => import('@admin-panel/pages/dashboard'),
  'HomeDashboard',
);
const ListVendors = lazyNamedExport(() => import('@admin-panel/pages/vendors'), 'ListVendors');
const VendorShow = lazyNamedExport(() => import('@admin-panel/pages/vendors'), 'VendorShow');
const SkuEnquiryHistory = lazyNamedExport(
  () => import('@admin-panel/pages/vendors'),
  'SkuEnquiryHistory',
);
const ListSkus = lazyNamedExport(() => import('@admin-panel/pages/skus'), 'ListSkus');
const ListWarehouseInventory = lazyNamedExport(
  () => import('@admin-panel/pages/inventory'),
  'ListWarehouseInventory',
);
const BetterInventory = lazyNamedExport<{
  headerButtons?: ReactNode;
  showInventoryControls?: boolean;
  showProductInventory?: boolean;
  showBarcodeScanner?: boolean;
}>(() => import('@admin-panel/pages/BetterInventory'), 'BetterInventory');
const StoreImages = lazyNamedExport<{ showShelfwiseImageQueue?: boolean }>(
  () => import('@admin-panel/pages/StoreImages'),
  'StoreImages',
);
const ShelfTransferPage = lazyNamedExport(
  () => import('@admin-panel/pages/shelf-transfer'),
  'ShelfTransferPage',
);
const ListOrderPackagesForWH = lazyNamedExport(
  () => import('@admin-panel/pages/orders'),
  'ListOrderPackagesForWH',
);
const OrderControlCenter = lazyNamedExport(
  () => import('@admin-panel/pages/orders'),
  'OrderControlCenter',
);
const ShowOrderPackage = lazyNamedExport(
  () => import('@admin-panel/pages/orders'),
  'ShowOrderPackage',
);
const EditDeliveryChallan = lazyNamedExport(
  () => import('@admin-panel/pages/dc'),
  'EditDeliveryChallan',
);
const ShowDeliveryChallan = lazyNamedExport<{ view?: string }>(
  () => import('@admin-panel/pages/dc'),
  'ShowDeliveryChallan',
);
const GRNList = lazyNamedExport(() => import('@admin-panel/pages/grn'), 'GRNList');
const ListMTOPurchaseOrders = lazyNamedExport(
  () => import('@admin-panel/pages/po'),
  'ListMTOPurchaseOrders',
);
const IncomingStockTransfersWrapper = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass'),
  'IncomingStockTransfersWrapper',
);
const OutgoingStockTransfersWrapper = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass'),
  'OutgoingStockTransfersWrapper',
);
const EditStockTransfer = lazyNamedExport(
  () => import('@admin-panel/pages/stocktransfer'),
  'EditStockTransfer',
);
const GatepassOrderShow = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass'),
  'GatepassOrderShow',
);
const GatepassScan = lazyNamedExport(() => import('@admin-panel/pages/gatepass'), 'GatepassScan');
const BulkPick = lazyNamedExport(() => import('@admin-panel/pages/gatepass'), 'BulkPick');
const BulkPickScan = lazyNamedExport(() => import('@admin-panel/pages/gatepass'), 'BulkPickScan');
const CreatePicklists = lazyNamedExport(
  () => import('@admin-panel/pages/picklist'),
  'CreatePicklists',
);
const Picklists = lazyNamedExport(() => import('@admin-panel/pages/picklist'), 'Picklists');
const OnDemandPick = lazyNamedExport(() => import('@admin-panel/pages/picklist'), 'OnDemandPick');
const OnDemandPickScan = lazyNamedExport(
  () => import('@admin-panel/pages/picklist'),
  'OnDemandPickScan',
);
const PicklistGatepasses = lazyNamedExport(
  () => import('@admin-panel/pages/picklist'),
  'PicklistGatepasses',
);
const IncomingManifestsWrapper = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass'),
  'IncomingManifestsWrapper',
);
const OutgoingManifestsWrapper = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass'),
  'OutgoingManifestsWrapper',
);
const CreateManifest = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass'),
  'CreateManifest',
);
const ShowManifest = lazyNamedExport(() => import('@admin-panel/pages/gatepass'), 'ShowManifest');
const CancelledAwbMovementExceptions = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/return'),
  'CancelledAwbMovementExceptions',
);
const ListReturnRequests = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/return'),
  'ListReturnRequests',
);
const ShowReturnRequest = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/return'),
  'ShowReturnRequest',
);
const ShowOrderLine = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/show'),
  'ShowOrderLine',
);
const ShowCustomerOrder = lazyNamedExport(
  () => import('@admin-panel/pages/orders'),
  'ShowCustomerOrder',
);
const CashHandover = lazyNamedExport(() => import('@admin-panel/pages/Logistics'), 'CashHandover');
const RiderAttendanceQr = lazyNamedExport(
  () => import('@admin-panel/pages/Logistics'),
  'RiderAttendanceQr',
);
const MyRides = lazyNamedExport(() => import('@admin-panel/pages/Logistics'), 'MyRides');
const MyRideDetails = lazyNamedExport(
  () => import('@admin-panel/pages/Logistics'),
  'MyRideDetails',
);
const RiderHealth = lazyNamedExport(() => import('@admin-panel/pages/Logistics'), 'RiderHealth');
const RiderDay = lazyNamedExport(() => import('@admin-panel/pages/Logistics'), 'RiderDay');
const RiderManagement = lazyNamedExport(
  () => import('@admin-panel/pages/Logistics'),
  'RiderManagement',
);
const OpenWorkMap = lazyNamedExport(() => import('@admin-panel/pages/Logistics'), 'OpenWorkMap');
const ReturnRideDetails = lazyNamedExport(
  () => import('@admin-panel/pages/Logistics'),
  'ReturnRideDetails',
);
const DashOrders = lazy(() => import('@admin-panel/pages/orders/DashOrders'));
const Logistics = lazyNamedExport(() => import('@admin-panel/pages/Logistics'), 'Logistics');
const RefundsOnhold = lazyNamedExport(
  () => import('@admin-panel/pages/orders/RefundsOnhold'),
  'RefundsOnhold',
);
const ShowRefundRequest = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/refund'),
  'ShowRefundRequest',
);
const ShowFacility = lazyNamedExport(() => import('@admin-panel/pages/facility'), 'ShowFacility');
const CycleCount = lazyNamedExport(() => import('@admin-panel/pages/cycle-count'), 'CycleCount');
const ShowB2BCustomer = lazyNamedExport(
  () => import('@admin-panel/pages/users'),
  'ShowB2BCustomer',
);
const PageNotFound = lazyNamedExport<{ reason?: 'unauthorized' | 'not_found' }>(
  () => import('@admin-panel/pages/PageNotFound'),
  'PageNotFound',
);

// Login allows the alarm sound and asks for the start-of-day briefing; logout retires this
// device's alert token so it stops ringing for the previous user.
const hubAuthProvider: AuthProvider = {
  ...LKAuthProvider,
  login: async params => {
    // The login tap is a user gesture: use it to allow the alarm sound (also on iPhone).
    void unlockHubAlarmAudio();
    const result = await LKAuthProvider.login(params);
    if (result.success) {
      // The first screen after login shows what is waiting, even if this device briefed today.
      markBriefAfterLogin();
    }
    return result;
  },
  logout: async params => {
    resetRegistrationStatus();
    // Loaded lazily so Firebase stays out of the main bundle.
    await import('./hubAlerts/hubPushRegistration')
      .then(({ closeHubNotifications, retireHubAlertTokenOnLogout }) =>
        Promise.all([closeHubNotifications(), retireHubAlertTokenOnLogout()]),
      )
      .catch(() => undefined);
    return LKAuthProvider.logout(params);
  },
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
    },
    mutations: {
      retry: 0,
    },
  },
});

function Application() {
  useIsOnline();

  const resources = useMemo(() => getResources('WAREHOUSE'), []);
  const accessControlClient = useMemo(() => new AccessControlClient(), []);

  return (
    <ReduxProvider store={store}>
      <ThemeProvider
        theme={{
          token: { colorPrimary: '#393AFB', colorBgLayout: '#ffffff' },
        }}
      >
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: '#393AFB',
              colorBgLayout: '#ffffff',
              fontFamily: '"Hanken Grotesk"',
              colorText: '#171717',
            },
          }}
        >
          <AntdApp>
            <ReleaseWatcher />
            <AppLinkHandler />
            <QueryClientProvider client={queryClient}>
              <Refine
                authProvider={hubAuthProvider}
                accessControlProvider={accessControlClient.getAccessControlProvider()}
                dataProvider={restDataProvider}
                notificationProvider={useNotificationProvider}
                routerProvider={routerProvider}
                options={{
                  syncWithLocation: true,
                  warnWhenUnsavedChanges: true,
                  projectId: 'Unny6d-jT5kpU-QOXAra',
                }}
                resources={resources}
              >
                <AccessControlConfig resources={resources} dataprovider={accessControlClient}>
                  <Routes>
                    <Route
                      element={
                        <Authenticated
                          key="authenticated-routes"
                          fallback={<CatchAllNavigate to="/login" />}
                        >
                          <GlobalProvider>
                            {/* <LkNotificationsProvider> */}
                            <HubAlertSetupProvider>
                              <SetupReporter />
                              <AlertCenterBoundary onError={discardInbox}>
                                <AlertCenter />
                              </AlertCenterBoundary>
                              <LKLayout headerTools={<HubHeaderTools />}>
                                <MainOutlet />
                              </LKLayout>
                            </HubAlertSetupProvider>
                            {/* </LkNotificationsProvider> */}
                          </GlobalProvider>
                        </Authenticated>
                      }
                    >
                      <Route index path="/" element={<HomeDashboard />} />
                      <Route path={SETUP_PATH} element={<HubAlertSetupPage />} />
                      <Route path="/packages">
                        <Route index path="" element={<ListOrderPackagesForWH />} />
                        <Route path="show/:id" element={<ShowOrderPackage />} />
                      </Route>
                      <Route index path="/ordercontrolcenter" element={<OrderControlCenter />} />
                      <Route
                        path="dash-orders"
                        element={
                          <CanAccess
                            resource={PATHS_NAMES.DASH_ORDERS}
                            action="list"
                            fallback={<PageNotFound reason="unauthorized" />}
                          >
                            <DashOrders />
                          </CanAccess>
                        }
                      />
                      <Route path="refunds-onhold" element={<RefundsOnhold />} />
                      <Route path="returns">
                        <Route path="" index element={<ListReturnRequests />} />
                        <Route
                          path="exceptions/cancelled-awb-movement"
                          element={<CancelledAwbMovementExceptions />}
                        />
                        <Route path="show/:id" index element={<ShowReturnRequest />} />
                      </Route>
                      <Route path="refunds">
                        <Route path="show/:id" element={<ShowRefundRequest />} />
                      </Route>
                      <Route path="/inventory">
                        <Route index path="" element={<ListWarehouseInventory />} />
                      </Route>
                      <Route index path="/cycle-count" element={<CycleCount />} />
                      <Route element={<Outlet />}>
                        <Route index element={null} />

                        <Route path="/vendors">
                          <Route index path="" element={<ListVendors />} />
                          <Route path="show/:id" element={<VendorShow />} />
                          <Route path="sku-enquiry-history" element={<SkuEnquiryHistory />} />
                        </Route>
                        <Route path="/skus">
                          <Route index path="" element={<ListSkus />} />
                        </Route>
                      </Route>

                      <Route element={<Outlet />}>
                        <Route path="/grn">
                          <Route index path="" element={<GRNList />} />
                          <Route
                            index
                            path="show/:id"
                            element={<ShowDeliveryChallan view="warehouse" />}
                          />
                          <Route index path="edit/:id" element={<EditDeliveryChallan />} />
                        </Route>
                        <Route path="mtopurchaseorders">
                          <Route index path="" element={<ListMTOPurchaseOrders />} />
                        </Route>
                        <Route path="incoming-stock-transfers">
                          <Route index path="" element={<IncomingStockTransfersWrapper />} />
                        </Route>
                        <Route path="outgoing-stock-transfers">
                          <Route index path="" element={<OutgoingStockTransfersWrapper />} />
                        </Route>
                        <Route path="stocktransfer">
                          <Route path="edit/:id" element={<EditStockTransfer />} />
                        </Route>
                        <Route path="incoming-manifests">
                          <Route index path="" element={<IncomingManifestsWrapper />} />
                        </Route>
                        <Route path="outgoing-manifests">
                          <Route index path="" element={<OutgoingManifestsWrapper />} />
                        </Route>
                        <Route path="manifests">
                          <Route path="create" element={<CreateManifest />} />
                          <Route path=":id/show" element={<ShowManifest />} />
                        </Route>
                        <Route path="gatepass-orders">
                          <Route path="show/:id" element={<GatepassOrderShow />} />
                        </Route>
                        <Route path="gatepasses">
                          <Route path=":gatepassId/scan" element={<GatepassScan />} />
                        </Route>
                        <Route path="bulk-pick">
                          <Route index element={<BulkPick />} />
                          <Route path="pick" element={<BulkPickScan />} />
                        </Route>
                        <Route path="picklists">
                          <Route index element={<Picklists />} />
                          <Route path="create" element={<CreatePicklists />} />
                          <Route path="gatepasses" element={<PicklistGatepasses />} />
                        </Route>
                        <Route path="on-demand-pick">
                          <Route index element={<OnDemandPick />} />
                          <Route path=":stockTransferId" element={<OnDemandPickScan />} />
                        </Route>
                        <Route path="lineitems/show/:id" element={<ShowOrderLine />} />
                        <Route path="orders/show/:id" element={<ShowCustomerOrder />} />
                        <Route path="facilities/show/:id" element={<ShowFacility />} />
                        <Route path="b2b-customer/show/:id" element={<ShowB2BCustomer />} />
                      </Route>

                      <Route path="/utility" element={<Outlet />}>
                        <Route
                          path="product-image-search"
                          element={
                            <BetterInventory
                              headerButtons={null}
                              showInventoryControls={false}
                              showProductInventory
                              showBarcodeScanner
                            />
                          }
                        />
                        <Route path="store-images">
                          <Route index path="" element={<StoreImages showShelfwiseImageQueue />} />
                        </Route>
                        <Route
                          path="shelf-transfer"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.SHELF_TRANSFER}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <ShelfTransferPage />
                            </CanAccess>
                          }
                        />
                      </Route>
                      <Route path="/logistics" element={<Logistics />}>
                        <Route
                          path="open-work-map"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.OPEN_WORK_MAP}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <OpenWorkMap />
                            </CanAccess>
                          }
                        />
                        <Route path="my-rides/:packageId" element={<MyRideDetails />} />
                        <Route path="my-rides/return/:returnId" element={<ReturnRideDetails />} />
                        <Route path="cash-handover" element={<CashHandover />} />
                        <Route
                          path="attendance-qr"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.RIDER_ATTENDANCE_QR}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <RiderAttendanceQr />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="riders"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.RIDER_MANAGEMENT}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <RiderManagement />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="rider-health"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.RIDER_HEALTH}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <RiderHealth />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="rider-health/:riderId/day"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.RIDER_HEALTH}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <RiderDay />
                            </CanAccess>
                          }
                        />
                        <Route index path="my-rides" element={<MyRides />} />
                      </Route>
                    </Route>
                    <Route
                      path="/forgot-password"
                      element={
                        <Suspense fallback={<FoundationLoader />}>
                          <WarehouseForgotPassword />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/reset-password"
                      element={
                        <Suspense fallback={<FoundationLoader />}>
                          <WarehouseResetPassword />
                        </Suspense>
                      }
                    />
                    <Route
                      element={
                        <Authenticated key="auth-pages" fallback={<Outlet />}>
                          <NavigateToResource resource="dashboard" />
                        </Authenticated>
                      }
                    >
                      <Route
                        path="/login"
                        element={
                          <Suspense fallback={<FoundationLoader />}>
                            <Login title="Login to Hub" />
                          </Suspense>
                        }
                      />
                    </Route>

                    <Route
                      element={
                        <Authenticated key="catch-all">
                          <ThemedLayoutV2>
                            <Outlet />
                          </ThemedLayoutV2>
                        </Authenticated>
                      }
                    >
                      <Route path="*" element={<ErrorComponent />} />
                    </Route>
                  </Routes>
                </AccessControlConfig>
              </Refine>
            </QueryClientProvider>
          </AntdApp>
        </ConfigProvider>
      </ThemeProvider>
    </ReduxProvider>
  );
}

const MainOutlet = () => {
  const { mode } = useTheme();
  const token = useAntdTheme();

  const { warehouseId, isAllFacilities } = useGlobalContext();
  const { pathname } = useLocation();
  // Alert setup works before a facility is chosen.
  const facilityReady = Boolean(warehouseId || isAllFacilities) || pathname === SETUP_PATH;

  return (
    <>
      <Suspense fallback={null}>
        <HubAlerts />
      </Suspense>
      <HubAlertSetupGate>
        {facilityReady && (
          <PageErrorBoundary resetKey={pathname}>
            <Suspense fallback={<FoundationLoader />}>
              <Outlet />
            </Suspense>
          </PageErrorBoundary>
        )}
        {!facilityReady && (
          <div
            style={{
              textAlign: 'center',
              padding: 16,
              backgroundColor: mode === 'light' ? token.colorBgContainer : '#000',
            }}
          >
            <Typography.Title>Please Choose a Facility</Typography.Title>
          </div>
        )}
      </HubAlertSetupGate>
    </>
  );
};

export default Application;
